import React, { useEffect, useState } from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Copy, Download, Loader2, RotateCcw } from 'lucide-react';
import { ESTADOS_PEDIDO, descargarArchivo, fmtCuit, fmtFecha, fmtFechaHora, fmtFechaHoraAR, fmtMoney, fmtNum } from './formato';

const Fila = ({ k, v }) => (
  <div className="flex gap-3 text-sm py-1 border-b border-slate-50 dark:border-slate-800/50">
    <span className="w-40 shrink-0 text-slate-500">{k}</span>
    <span className="text-slate-800 dark:text-slate-100 break-all">{v || '—'}</span>
  </div>
);

const filtrosTexto = (f) => [
  f.sentido === 'INGRESO' ? 'solo ingresos' : f.sentido === 'EGRESO' ? 'solo egresos' : null,
  f.monto_min != null ? `desde ${fmtMoney(f.monto_min)}` : null,
  f.monto_max != null ? `hasta ${fmtMoney(f.monto_max)}` : null,
  f.contraparte ? `contraparte ${f.contraparte}` : null,
].filter(Boolean).join(', ') || 'sin filtros';

export const PedidoDetalleDialog = ({ pedidoId, onClose, onCambio }) => {
  const [p, setP] = useState(null);
  const [bajando, setBajando] = useState(null);

  const cargar = () => api.get(`/export-movimientos/pedidos/${pedidoId}`).then((r) => setP(r.data)).catch(() => setP(null));
  useEffect(() => { if (pedidoId) { setP(null); cargar(); } }, [pedidoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const bajar = async (fmt) => {
    setBajando(fmt);
    try { await descargarArchivo(api, p, fmt); await cargar(); onCambio(); } catch { toast.error('No se pudo descargar'); } finally { setBajando(null); }
  };
  const reintentar = async () => {
    try { await api.post(`/export-movimientos/pedidos/${p.id}/reintentar`); toast.success('Reintentando'); onCambio(); onClose(); } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo reintentar'); }
  };

  const est = p ? ESTADOS_PEDIDO[p.estado] || ESTADOS_PEDIDO.GENERANDO : null;
  return (
    <Dialog open={!!pedidoId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="expmov-detalle-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {p ? p.numero : 'Pedido'}
            {est && <Badge className={`border-0 ${est.cls}`} data-testid="expmov-detalle-estado">{est.label}</Badge>}
          </DialogTitle>
        </DialogHeader>
        {!p ? <div className="py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div> : (
          <div className="space-y-4">
            <div>
              <Fila k="Titular" v={p.titular} />
              <Fila k="CUIT/CUIL" v={p.cuit ? fmtCuit(p.cuit) : null} />
              <Fila k="Buscado por" v={`${p.tipo_objetivo} ${p.objetivo}`} />
              <Fila k="Cuentas" v={p.filtros.cvus.join(', ')} />
              <Fila k="Período" v={`${fmtFecha(p.filtros.desde)} al ${fmtFecha(p.filtros.hasta)}`} />
              <Fila k="Filtros" v={filtrosTexto(p.filtros)} />
              <Fila k="Motivo" v={p.motivo_label} />
              <Fila k="Referencia" v={p.referencia} />
              <Fila k="Comentario" v={p.comentario} />
              <Fila k="Pedido por" v={`${p.usuario_nombre} (${p.usuario_email}) · ${fmtFechaHora(p.created_at_ar)}`} />
              {p.resumen && <Fila k="Resultado" v={`${fmtNum(p.resumen.cantidad)} movimientos · ingresos ${fmtMoney(p.resumen.total_ingresos)} · egresos ${fmtMoney(p.resumen.total_egresos)}`} />}
              {p.error && <Fila k="Error" v={<span className="text-red-600">{p.error}</span>} />}
              {(p.avisos || []).map((a) => <Fila key={a} k="Aviso" v={<span className="text-amber-700">{a}</span>} />)}
            </div>

            {p.estado === 'LISTO' && (
              <div className="space-y-2">
                {(p.archivos || []).map((a) => (
                  <div key={a.formato} className="space-y-1">
                    <Button size="sm" variant="outline" onClick={() => bajar(a.formato)} disabled={!!bajando} data-testid={`expmov-detalle-bajar-${a.formato}`}>
                      {bajando === a.formato ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} {a.nombre}
                    </Button>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
                      <span title="Huella del archivo: permite comprobar que no fue modificado">SHA-256</span>
                      <code className="font-mono break-all text-slate-600" data-testid={`expmov-detalle-sha-${a.formato}`}>{a.sha256}</code>
                      <button type="button" className="text-slate-400 hover:text-slate-700" title="Copiar"
                        onClick={() => { navigator.clipboard?.writeText(a.sha256); toast.success('Huella copiada'); }} data-testid={`expmov-detalle-copiar-sha-${a.formato}`}>
                        <Copy className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {p.estado === 'ERROR' && (
              <Button size="sm" variant="outline" onClick={reintentar} data-testid="expmov-detalle-reintentar"><RotateCcw className="h-4 w-4 mr-1" /> Reintentar</Button>
            )}

            <div>
              <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">Descargas ({(p.descargas || []).length})</h3>
              {(p.descargas || []).length === 0 ? <p className="text-xs text-slate-500">Todavía nadie lo descargó.</p> : (
                <table className="w-full text-xs" data-testid="expmov-detalle-descargas">
                  <tbody>
                    {[...p.descargas].reverse().map((d, i) => (
                      <tr key={i} className="border-b border-slate-50 dark:border-slate-800/50">
                        <td className="py-1 pr-3 whitespace-nowrap">{fmtFechaHoraAR(d.fecha)}</td>
                        <td className="py-1 pr-3">{d.usuario_nombre} <span className="text-slate-400">({d.usuario_email})</span></td>
                        <td className="py-1 pr-3">{d.formato}</td>
                        <td className="py-1 font-mono text-slate-400">{d.ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PedidoDetalleDialog;
